import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// Define the color palette for admin panel (light mode)
const adminColors = {
  primary: {
    main: '#2E7D32', // More subdued green for better contrast
    light: '#4CAF50',
    dark: '#1B5E20',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#1565C0', // Blue as secondary accent
    light: '#1E88E5',
    dark: '#0D47A1',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#F8F9FA', // Very light gray background
    paper: '#FFFFFF',   // White for cards/elements
    elevated: '#FFFFFF', // Also white for elevated components
  },
  text: {
    primary: '#212121', // Very dark gray for primary text (almost black)
    secondary: '#424242', // Dark gray for secondary text
    disabled: '#9E9E9E', // Medium gray for disabled text
  },
  divider: 'rgba(0, 0, 0, 0.12)',
};

// Create the admin theme
let adminTheme = createTheme({
  palette: {
    mode: 'light',
    primary: adminColors.primary,
    secondary: adminColors.secondary,
    background: {
      default: adminColors.background.default,
      paper: adminColors.background.paper,
    },
    text: adminColors.text,
    divider: adminColors.divider,
  },
  typography: {
    fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: '2.5rem',
      letterSpacing: '0.01em',
      color: adminColors.text.primary,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      letterSpacing: '0.01em',
      color: adminColors.text.primary,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      letterSpacing: '0.01em',
      color: adminColors.text.primary,
    },
    h4: {
      fontWeight: 500,
      fontSize: '1.5rem',
      letterSpacing: '0.01em',
      color: adminColors.text.primary,
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
      letterSpacing: '0.01em',
      color: adminColors.text.primary,
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.125rem',
      letterSpacing: '0.01em',
      color: adminColors.text.primary,
    },
    body1: {
      fontWeight: 400,
      fontSize: '1rem',
      letterSpacing: '0.01em',
      lineHeight: 1.5,
      color: adminColors.text.primary,
    },
    body2: {
      fontWeight: 400,
      fontSize: '0.875rem',
      letterSpacing: '0.01em',
      lineHeight: 1.5,
      color: adminColors.text.secondary,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1rem',
      color: adminColors.text.primary,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.875rem',
      color: adminColors.text.secondary,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: adminColors.background.default,
          color: adminColors.text.primary,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: adminColors.primary.main,
          color: '#FFF',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          borderRight: `1px solid ${adminColors.divider}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: `rgba(46, 125, 50, 0.12)`,
            '&:hover': {
              backgroundColor: `rgba(46, 125, 50, 0.18)`,
            },
          },
          '&:hover': {
            backgroundColor: `rgba(0, 0, 0, 0.04)`,
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: adminColors.text.primary,
          minWidth: '40px', // Make icons slightly more compact
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: `rgba(46, 125, 50, 0.12)`,
            color: adminColors.primary.main,
            '&:hover': {
              backgroundColor: `rgba(46, 125, 50, 0.18)`,
            },
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.MuiTypography-colorTextSecondary': {
            color: adminColors.text.secondary,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: adminColors.text.primary,
          padding: '12px 16px',
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#F5F5F5',
          color: adminColors.text.primary,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: adminColors.background.paper,
          color: adminColors.text.primary,
        },
      },
    },
  },
});

// Make fonts responsive
adminTheme = responsiveFontSizes(adminTheme);

export default adminTheme; 